import React from 'react';
import _invoke from 'lodash/invoke';
import _pick from 'lodash/pick';

export const LOCAL_STORAGE_KEY = 'osp:tabs-router';

/**
 * Use it as React hook on the page where Tabs work with Router.
 * Example:
 * const MyPage = (props) => {
 *   const [value, setValue, returnToPrevPage] = useTabsRouter({ tabs, defaultTabIndex: 1, history, match, location });
 *
 *   ... your code here
 *
 *   return (
 *    <div>My Page</div>
 *   );
 * };
 *
 * tabs: [{
 *   label: 'overview',
 *   content: 'Overview',
 *   route: '/overview',
 *   disabled: true,
 * },
 * {
 *   label: 'groups',
 *   content: <GroupsPage />,
 *   route: '/groups',
 *   disabled: false,
 * },
 *  ...],
 *
 * defaultTabIndex: (number - start from 0)
 * returnToPrevPage - navigates user back to previous page regardless how many tabs navigations are occurred
 */
export const useTabsRouter = ({ tabs, defaultTabIndex, history, match, location, skipStateMemoization }) => {
  const tabsState = React.useRef(null);

  if (tabsState.current === null) {
    try {
      tabsState.current = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_KEY)) || {};
    } catch (e) {
      tabsState.current = {};
    }
  }

  const [tabValue, setTabValue] = React.useState(() => {
    // need it for page reload
    const path = location.pathname || '';
    let tabIndex = tabs.findIndex((t) => path.includes(t.route));
    if (tabIndex === -1) {
      tabIndex = defaultTabIndex;
    }
    return tabIndex;
  });

  const returnToPrevPage = () => history.goBack();

  React.useEffect(() => {
    // change tab on location change (ex. back/forward browser buttons)
    const path = location.pathname || '';
    const tabIndex = tabs.findIndex((t) => path.includes(t.route));

    if (tabIndex !== tabValue && tabIndex !== -1) {
      setTabValue(tabIndex);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    // change location on tab change
    if (tabValue === -1) {
      return;
    }

    const currentPath = location.pathname;
    const newPath = `${match?.url}${tabs[tabValue].route}`;

    if (currentPath === newPath) {
      return;
    }

    const currentTab = tabs.find((tab) => currentPath?.includes(tab.route));

    // notify component that is rendered within current that it will be unmounted
    _invoke(currentTab, ['tabRef', 'current', 'onTabLeave']);

    let nextTabState = {};

    if (!skipStateMemoization) {
      // retrieve tab state for next transition tab in case it was visited (cached) before
      // Also we need to removed retrieved state from storage since it's gonna be passed to location state
      if (tabsState.current[newPath]) {
        nextTabState = tabsState.current[newPath];

        delete tabsState.current[newPath];
      }

      // save location search and state for current path later it can be restored if the path will match previously saved
      tabsState.current[currentPath] = _pick(history.location, ['search', 'state']);

      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tabsState.current));
    }

    history.replace({
      pathname: newPath,
      ..._pick(nextTabState, ['search', 'state']),
    });

    return () => {
      // clear the cached tabs data as soon current page is left
      window.localStorage.removeItem(LOCAL_STORAGE_KEY);
    };
  }, [tabValue]);

  return [tabValue, setTabValue, returnToPrevPage];
};

export default useTabsRouter;
